import {
  SystemCssProperties,
  SystemStyleObject,
} from '@mui/system/styleFunctionSx/styleFunctionSx';
import { Box, Stack, SxProps } from '@mui/material';
import { ReactNode, RefObject, useMemo, useRef } from 'react';

import { getItemDimensions, getTimeLabel } from './utils';
import { HoverAndFlowAnchors, useHoverAndFlow } from './use-hover-and-flow.hook';

import { Typography } from '../../../typography/typography.component';
import { CalendarGridDayTimeLineItem, CalendarGridTimeMode } from '../../types';
import { CALENDAR_GRID_DEFAULTS } from '../../constants';
import { normaliseSx } from '../../utils';

const SMALL_ITEM_HEIGHT = 20;
const SMALL_ITEM_PADDING_SPC = 0.3;
const NORMAL_ITEM_PADDING_SPC = 0.6;
const INFO_X_OFFSET = 8;
const INFO_Y_OFFSET = 8;
const INFO_POINTER_OFFSET = 20;

type CalendarGridDayItemProps = CalendarGridDayTimeLineItem & {
  timeLaneIndex: number;
  timeLaneRatio?: number;
  timeMode?: CalendarGridTimeMode;
  gridStep: number;
  gridWidth: number;
  color?: string;
  textColor?: string;
  onClick?: () => void;
  sx?: SxProps;
  workingAreaRef?: RefObject<HTMLDivElement>;
  renderAction?: (item: CalendarGridDayTimeLineItem, textColor?: string) => ReactNode;
  children?: ReactNode;
  renderContent?: (
    anchors: HoverAndFlowAnchors,
    pointerOffset: number,
    maxWidth: number,
    maxHeight: number,
  ) => ReactNode;
};

export const CalendarGridDayItem = ({
  id,
  header,
  footer,
  subFooter,
  data,
  timeLaneIndex,
  timeLaneRatio = 1,
  gridStep,
  gridWidth,
  startTime,
  endTime,
  timeMode = '12H',
  color = CALENDAR_GRID_DEFAULTS.dayItemColors[0],
  textColor = CALENDAR_GRID_DEFAULTS.dayItemTextColors[0],
  onClick,
  sx,
  workingAreaRef,
  children,
  renderAction,
  renderContent,
}: CalendarGridDayItemProps) => {
  const { pos, height, spaceBelow, isSmallItem } = useMemo(() => {
    const data = getItemDimensions(startTime, endTime, gridStep);
    return { ...data, isSmallItem: data.height <= SMALL_ITEM_HEIGHT };
  }, [endTime, gridStep, startTime]);

  const itemRef = useRef<HTMLDivElement>(null);
  const infoRef = useRef<HTMLDivElement>(null);
  const { anchors } = useHoverAndFlow(itemRef, infoRef, {
    offsetX: INFO_X_OFFSET,
    offsetY: INFO_Y_OFFSET,
    pointerOffset: INFO_POINTER_OFFSET,
    displayProp: 'flex',
    workingAreaRef: workingAreaRef,
  });

  const label = useMemo(() => {
    const parts = getTimeLabel(startTime, endTime, timeMode).split(' - ');
    return (
      <>
        <span>{parts[0]}</span>
        <span> - </span>
        <span>{parts[1]}</span>
      </>
    );
  }, [startTime, endTime, timeMode]);

  const paddingTop = height <= SMALL_ITEM_HEIGHT ? SMALL_ITEM_PADDING_SPC : NORMAL_ITEM_PADDING_SPC;

  return (
    <>
      <Stack
        ref={itemRef}
        top={pos}
        left={`${timeLaneIndex * timeLaneRatio * 100}%`}
        height={height}
        borderRadius={1}
        onClick={onClick}
        sx={[
          containerStyle,
          ...normaliseSx(sx),
          {
            width: `${timeLaneRatio * 100}%`,
            cursor: onClick ? 'pointer' : 'auto',
            backgroundColor: color,
          },
        ]}
      >
        <Box px={1} pt={paddingTop} display='flex' flexDirection='column'>
          {isSmallItem && header ? null : (
            <Typography variant='caption' sx={[textStyle, { color: textColor }]}>
              {label}
            </Typography>
          )}
          {!!header && (
            <Typography variant='caption' fontWeight={700} sx={[textStyle, { color: textColor }]}>
              {header}
            </Typography>
          )}
          {renderAction && (
            <Box pt={0.5}>
              {renderAction(
                { id, startTime, endTime, header, footer, subFooter, data, timeLaneIndex },
                textColor,
              )}
            </Box>
          )}
        </Box>
        {isSmallItem ? null : (
          <Box px={1} pb={paddingTop} display='flex' flexDirection='column'>
            {!!footer && (
              <Typography variant='caption' sx={[textStyle, { color: textColor }]}>
                {footer}
              </Typography>
            )}
            {!!subFooter && (
              <Typography variant='caption' fontWeight={700} sx={[textStyle, { color: textColor }]}>
                {subFooter}
              </Typography>
            )}
          </Box>
        )}
      </Stack>
      {(!!children || renderContent) && (
        <Box
          ref={infoRef}
          position='absolute'
          display='none'
          flexDirection='column'
          zIndex={1}
          sx={{
            transform: `translateY(${
              isSmallItem
                ? (anchors.vertical === 'bottom' ? 1 : -1) *
                  (INFO_POINTER_OFFSET + INFO_Y_OFFSET - height / 2)
                : 0
            }px)`,
          }}
        >
          {children}
          {renderContent?.(
            anchors,
            INFO_POINTER_OFFSET,
            gridWidth * 2,
            (Math.max(pos, spaceBelow) || height / 2) - INFO_Y_OFFSET,
          )}
        </Box>
      )}
    </>
  );
};

const containerStyle: SystemCssProperties = {
  justifyContent: 'space-between',
  position: 'absolute',
  boxSizing: 'border-box',
  border: '1px solid transparent',
  clipPath: 'padding-box',
};

const textStyle: SystemStyleObject = {
  display: 'inline-block',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  width: '100%',
  lineHeight: '1.3',
  '& > span': {
    display: 'inline-block',
    whiteSpace: 'break-spaces',
  },
};
